/**
 * Overlay — the app's ONLY two overlay shells. Standard ratified 2026-07-27
 * (see `docs/overlay-standard.md`; report: "Overlay Standard — Drawer vs Modal").
 *
 *   <Drawer>  a THING — one record, or anything with tabs / a list / scrolling.
 *   <Modal>   a DECISION — short, blocking, finish-or-abandon. ≤480px, no scroll,
 *             no tabs. If it needs any of those, the test has already answered:
 *             it is a Drawer.
 *
 * Responsive (one rule, three renderings):
 *   Drawer  desktop right slide-over · tablet 85% · phone full-screen push + back
 *   Modal   desktop/tablet centred   · phone bottom sheet
 *
 * Operator rulings baked in:
 *   1. Drawers REPLACE, never stack — opening a second hides the first and the
 *      header shows a back arrow that returns to it. One drawer on screen, ever.
 *   2. Every drawer is URL-addressable (`?panel=<key>`), pushed as a history
 *      entry so the browser/hardware back button closes it. Essential on phone,
 *      where a drawer reads as a pushed screen.
 *
 * Do not hand-roll another overlay. Adding one is a standard violation.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon, T, alpha } from "@v0y4ge/jrni-ui";
import { useT } from "../lib/i18n";
import { useViewport } from "./useResponsive";

/* ── Entrance animation ──────────────────────────────────────────────────
 * CSS keyframes, not a rAF-driven state flip. The previous implementation set
 * `shown` from inside requestAnimationFrame and drove transform off it; when
 * anything cancelled or outran that frame the panel stayed parked at
 * translateX(100%) with a fully transparent scrim — the button looked dead.
 * (Seen twice: the comment this replaces documents the first occurrence, and
 * 2026-07-29 the MFA enrollment drawer reproduced it: `shown` never flipped,
 * measured stuck at 785px across 8 samples.) A keyframe runs when the element
 * is inserted and cannot get stuck mid-flight the way the flag did.
 *
 * BUT an entrance keyframe is only safe where the timeline actually advances.
 * At `currentTime 0` the animation is INSIDE its active phase, so the
 * from-keyframe (translateX(100%) / opacity 0) applies no matter what fill mode
 * is set — and a document whose timeline never advances (hidden tab, snapshot /
 * print capture, the verification harness) sits at 0 forever and paints the
 * panel off-viewport. Measured: transform matrix(…785.391, 0) at currentTime 0;
 * the same panel with the animation cancelled renders correctly in place. So the
 * animation is an ENHANCEMENT, attached only when the document can animate; the
 * resting style is already the final state. The global prefers-reduced-motion
 * rule in jrni-tokens/reset.css collapses the duration for users who ask. */
function canAnimateNow(): boolean {
  if (typeof document === "undefined") return false;
  if (document.visibilityState !== "visible") return false;
  const tl = document.timeline as { currentTime?: number | null } | undefined;
  return (tl?.currentTime ?? 0) > 0;
}
const OVERLAY_ANIM_ID = "om-overlay-anim";
if (typeof document !== "undefined" && !document.getElementById(OVERLAY_ANIM_ID)) {
  const el = document.createElement("style");
  el.id = OVERLAY_ANIM_ID;
  el.textContent =
    "@keyframes omDrawerIn{from{transform:translateX(100%)}to{transform:translateX(0)}}" +
    "@keyframes omModalIn{from{transform:translateY(6px);opacity:0}to{transform:translateY(0);opacity:1}}" +
    "@keyframes omSheetIn{from{transform:translateY(12px);opacity:0}to{transform:translateY(0);opacity:1}}";
  document.head.appendChild(el);
}

/* ── Single-drawer registry (ruling 1: replace, don't stack) ─────────── */

let STACK: string[] = [];
const SUBS = new Set<() => void>();
const emit = () => SUBS.forEach((f) => { try { f(); } catch { /* noop */ } });
function useDrawerStack(id: string): { top: boolean; beneath: boolean } {
  const [, force] = useState(0);
  useEffect(() => {
    STACK = [...STACK, id];
    emit();
    const fn = () => force((n) => n + 1);
    SUBS.add(fn);
    return () => { STACK = STACK.filter((x) => x !== id); SUBS.delete(fn); emit(); };
  }, [id]);
  const idx = STACK.indexOf(id);
  return { top: idx === STACK.length - 1, beneath: idx > 0 };
}

/* ── URL binding (ruling 2) ──────────────────────────────────────────── */

function usePanelUrl(panel: string | undefined, active: boolean, onClose: () => void) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!panel || !active) return;
    let pushed = false;
    try {
      const sp = new URLSearchParams(window.location.search);
      sp.set("panel", panel);
      window.history.pushState({ ...(window.history.state || {}), __panel: panel }, "", window.location.pathname + "?" + sp.toString());
      pushed = true;
    } catch { /* opaque origin — drawer still works, just not linkable */ }
    const onPop = () => closeRef.current();
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      // Closing by button/scrim/Escape must also retire the history entry, or
      // Back would re-open a drawer the user just dismissed.
      if (!pushed) return;
      try {
        const sp = new URLSearchParams(window.location.search);
        if (sp.get("panel") === panel) window.history.back();
      } catch { /* noop */ }
    };
  }, [panel, active]);
}

/* ── Shared behaviour ────────────────────────────────────────────────── */

/* ── Portal (2026-07-29) ─────────────────────────────────────────────────
 * Both shells MUST mount at document.body. Rendered in place, an overlay sits
 * wherever its caller lives in the tree — and any ancestor that creates a
 * stacking context (a `position: sticky` wrapper does, silently, even at
 * z-index auto) caps the overlay's z-index inside that context. Measured on the
 * My Day walk-in drawer: the panel painted correctly at fixed/z-300 but the app
 * topbar (z 50 in a sibling context) covered its header — title and close button
 * unreachable — and the page's talking-points card punched through the panel
 * body. A portal is the only fix that holds for every future caller. */
function Portal({ children }: { children: React.ReactNode }) {
  const [host, setHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const el = document.createElement("div");
    el.setAttribute("data-overlay-portal", "");
    document.body.appendChild(el);
    setHost(el);
    return () => { el.remove(); };
  }, []);
  return host ? createPortal(children, host) : null;
}

/** The same body-mounted host, for a full-screen surface that isn't a Drawer or
 *  a Modal but hits the identical wall — e.g. the Journey designer's customer
 *  preview, whose header (and its Close button) sat UNDER the app topbar because
 *  it painted in place inside the screen's own stacking context. */
export { Portal as OverlayPortal };

function useOverlayChrome(active: boolean, onClose: () => void) {
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [active, onClose]);
}

export interface DrawerProps {
  /** Stable key for the URL (`?panel=<key>`) — omit only for nested previews. */
  panel?: string;
  title: string;
  sub?: string;
  icon?: React.ReactNode;
  /**
   * Rich header. When supplied it REPLACES the standard title row, so a panel
   * with its own identity band (avatar, tier, brand ink) keeps that look while
   * still riding the shared shell — same panel URL, back behaviour, stacking and
   * responsive rules. The hero owns its own close affordance.
   */
  hero?: React.ReactNode;
  /** Merged into the scrolling body (e.g. a canvas background behind cards). */
  bodyStyle?: React.CSSProperties;
  /** Desktop width. Tablet uses 85%, phone is always full-screen. */
  width?: number;
  onClose: () => void;
  footer?: React.ReactNode;
  children: React.ReactNode;
  testid?: string;
}

export function Drawer({ panel, title, sub, icon, hero, bodyStyle, width = 480, onClose, footer, children, testid }: DrawerProps) {
  const t = useT();
  const vp = useViewport();
  const idRef = useRef(`dw_${Math.random().toString(36).slice(2)}`);
  const { top, beneath } = useDrawerStack(idRef.current);
  const animRef = useRef(canAnimateNow());
  const close = useCallback(() => onClose(), [onClose]);
  useOverlayChrome(top, close);
  usePanelUrl(panel, true, close);

  const phone = vp === "phone";
  const panelWidth = phone ? "100%" : vp === "tablet" ? "85%" : `${width}px`;

  return (
    <Portal>
    <div role="dialog" aria-modal="true" aria-label={title} onClick={close} data-overlay="drawer"
      style={{ position: "fixed", inset: 0, zIndex: 300, display: top ? "flex" : "none", justifyContent: "flex-end",
        background: alpha(T.n1, 45) }}>
      <div onClick={(e) => e.stopPropagation()} data-testid={testid}
        style={{ width: panelWidth, maxWidth: "100%", height: "100%", background: T.white, boxShadow: T.shadowLg,
          display: "flex", flexDirection: "column", fontFamily: T.font,
          animation: animRef.current ? "omDrawerIn 220ms cubic-bezier(.2,0,0,1)" : undefined }}>
        {hero ? <div style={{ flexShrink: 0 }}>{hero}</div> : (
        <div style={{ padding: phone ? "12px 14px" : "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          {/* Phone reads a drawer as a pushed screen, and a replaced drawer owes
              the user a way back — both get a leading back arrow. */}
          {phone || beneath ? (
            // ui-raw ×3 in this file (back + both closes): borderless 30/32px icon affordances in an overlay header. IconButton always draws a border; ghost icon-button variant requested.
            <button aria-label={t("common.actions.back", "Back")} onClick={close} data-testid="overlay-back"
              style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: "transparent", color: T.n1, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon.arrow dir="left" size={15} />
            </button>
          ) : icon ? (
            <span aria-hidden="true" style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0, display: "grid", placeItems: "center", background: T.p4, color: T.p1 }}>{icon}</span>
          ) : null}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: phone ? 15.5 : 17, fontWeight: 700, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
            {sub ? <div style={{ fontSize: 12.5, color: T.soft, marginTop: 3, lineHeight: 1.5 }}>{sub}</div> : null}
          </div>
          {!phone ? (
            // ui-raw: borderless icon affordance (see the back button above).
            <button aria-label={t("common.actions.close", "Close")} onClick={close} data-testid="overlay-close"
              style={{ width: 30, height: 30, borderRadius: 8, border: "none", background: "transparent", color: T.n2, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.x size={14} /></button>
          ) : null}
        </div>
        )}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: phone ? "16px 14px" : 20, display: "flex", flexDirection: "column", gap: 18, ...bodyStyle }}>{children}</div>
        {footer ? (
          <div style={{ padding: phone ? "12px 14px" : "12px 20px", borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "flex-end", gap: 10, flexShrink: 0, background: T.white }}>{footer}</div>
        ) : null}
      </div>
    </div>
    </Portal>
  );
}

export interface ModalProps {
  title: string;
  sub?: string;
  icon?: React.ReactNode;
  tone?: "primary" | "danger";
  onClose: () => void;
  footer: React.ReactNode;
  children: React.ReactNode;
  testid?: string;
}

/**
 * A DECISION. Hard-capped at 480px with no internal scroll region and no tabs —
 * the cap is the enforcement mechanism, not a style preference. Content that
 * outgrows it belongs in a <Drawer>. On phone it becomes a bottom sheet so the
 * buttons land in the thumb zone.
 */
export function Modal({ title, sub, icon, tone = "primary", onClose, footer, children, testid }: ModalProps) {
  const t = useT();
  const phone = useViewport() === "phone";
  const animRef = useRef(canAnimateNow());
  useOverlayChrome(true, onClose);
  const accent = tone === "danger" ? T.red1 : T.p1;
  return (
    <Portal>
    <div role="dialog" aria-modal="true" aria-label={title} onClick={onClose} data-overlay="modal"
      style={{ position: "fixed", inset: 0, zIndex: 320, background: alpha(T.n1, 45),
        display: "flex", alignItems: phone ? "flex-end" : "center", justifyContent: "center", padding: phone ? 0 : 16 }}>
      <div onClick={(e) => e.stopPropagation()} data-testid={testid}
        style={{ width: phone ? "100%" : 480, maxWidth: "100%", background: T.white, fontFamily: T.font,
          borderRadius: phone ? "16px 16px 0 0" : 14, boxShadow: T.shadowLg, overflow: "hidden",
          animation: animRef.current ? `${phone ? "omSheetIn" : "omModalIn"} 180ms cubic-bezier(.2,0,0,1)` : undefined }}>
        {phone ? <div aria-hidden="true" style={{ width: 36, height: 4, borderRadius: 999, background: T.n4, margin: "8px auto 0" }} /> : null}
        <div style={{ padding: "16px 20px", display: "flex", alignItems: "flex-start", gap: 12 }}>
          {icon ? <span aria-hidden="true" style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0, display: "grid", placeItems: "center", background: alpha(accent, 12), color: accent }}>{icon}</span> : null}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: T.n1 }}>{title}</div>
            {sub ? <div style={{ fontSize: 12.5, color: T.soft, marginTop: 3, lineHeight: 1.5 }}>{sub}</div> : null}
          </div>
          {/* ui-raw: borderless icon affordance (see above). */}
          <button aria-label={t("common.actions.close", "Close")} onClick={onClose} data-testid="overlay-close"
            style={{ width: 30, height: 30, borderRadius: 8, border: "none", background: "transparent", color: T.n2, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.x size={14} /></button>
        </div>
        <div style={{ padding: "0 20px 16px", display: "flex", flexDirection: "column", gap: 16 }}>{children}</div>
        <div style={{ padding: "12px 20px", borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "flex-end", gap: 10, background: T.white }}>{footer}</div>
      </div>
    </div>
    </Portal>
  );
}
